import {
  getTeamMemberListData,
  _getTMDataById,
  _getTMSelectItemDataById,
} from '../teamMembers/mockData'
import {
  getProjectListData,
  _getProjSelectItemDataById,
} from '../projects/mockData'
import {
  getLocationListData,
  _getLSelectItemDataById,
} from '../locations/mockData'
import {
  getSupplierListData,
  _getSSelectItemDataById,
} from '../suppliers/mockData'
import { getTsdListData, _getTSelectItemDataById } from '../tsds/mockData'
import { getTsdContactListData, _getTCTDataById } from '../tsdContacts/mockData'
import {
  getOpportunityListData,
  _getOSelectItemDataById,
} from '../opportunities/mockData'
import { getOrderListData, _getMOSelectItemDataById } from '../orders/mockData'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '../companyContacts/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getSupplierContactListData,
  _getSCTDataById,
} from './../supplierContacts/mockData'
import { getPriorityById, PriorityIds } from '~/services/selectLists/priorities'
import {
  getEmailStatusById,
  EmailStatusIds,
} from '~/services/selectLists/emailStatii'
import type { IActivityEmail } from '~/types/email/models'
import type { IEntitySelectItem, TEntityId } from '~/types/models'

const emailListData = undefined as unknown as IActivityEmail[]

export const getEmailListData = getListDataFunctionFactory<IActivityEmail[]>({
  listData: emailListData,
  storageKey: 'emails',
  getStartingListData: getStartingEmailListData,
  sortFunction: (a, b) => {
    if (b.createdAt < a.createdAt) {
      return -1
    }
    if (b.createdAt > a.createdAt) {
      return 1
    }
    return 0
  },
})

export async function getEmailDataById(
  id: TEntityId
): Promise<IActivityEmail | undefined> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return list.find((email: IActivityEmail) => email.id === id)
}

export async function getEmailListDataByCompanyId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return (
    list.filter(
      (email: IActivityEmail) =>
        email.company?.id === id || email.contact?.company?.id === id
    ) ?? []
  )
}

export async function getEmailListDataByOpportunityId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return (
    list.filter((email: IActivityEmail) => email.opportunity?.id === id) ?? []
  )
}

export async function getEmailListDataByOrderId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return (
    list.filter(
      (email: IActivityEmail) => email.order && email.order.id === id
    ) ?? []
  )
}

export async function getEmailListDataByProjectId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return (
    list.filter(
      (email: IActivityEmail) => email.project && email.project.id === id
    ) ?? []
  )
}

export async function getEmailListDataByLocationId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  return (
    list.filter(
      (email: IActivityEmail) => email.location && email.location.id === id
    ) ?? []
  )
}

export async function getEmailListDataByOwnerId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  const emails =
    list.filter((e: IActivityEmail) => e.owner && e.owner.id === id) ?? []
  return emails
}

export async function getEmailListDataBySupplierId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  const emails =
    list.filter(
      (e: IActivityEmail) =>
        e.supplier?.id === id || e.contact?.supplier?.id === id
    ) ?? []
  return emails
}

export async function getEmailListDataByTsdId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  const emails =
    list.filter(
      (e: IActivityEmail) => e.tsd?.id === id || e.contact?.tsd?.id === id
    ) ?? []
  return emails
}

export async function getEmailListDataByTeamMemberId(
  id: TEntityId
): Promise<IActivityEmail[]> {
  const list = await getEmailListData()
  if (!list) {
    throw new Error(`emailList returned undefined/null`)
  }
  const emails =
    list.filter(
      (e: IActivityEmail) => e.owner?.id === id || e.assignedTo?.id === id
    ) ?? []
  return emails
}

// Every seed but the last is an email RECORDED as sent (`sentAt` is the
// user's), not one Cynque sent: no `direction`, and no attachments.
export async function getStartingEmailListData(): Promise<IActivityEmail[]> {
  const tmList = await getTeamMemberListData()
  const projList = await getProjectListData()
  const cctList = await getCompanyContactListData()
  const sctList = await getSupplierContactListData()
  const locList = await getLocationListData()
  const sList = await getSupplierListData()
  const tList = await getTsdListData()
  const tctList = await getTsdContactListData()
  const oList = await getOpportunityListData()
  const moList = await getOrderListData()

  return [
    {
      id: 'XXEM0001',
      hrid: 'EM0001',
      project: _getProjSelectItemDataById('XXP0002', projList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Scheduled DIA Install',
      body: 'This is the first email body associated with E-LAN Migration',
      notes: 'This is the first email associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T17:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-01-02T17:15:00.000Z',
      modifiedAt: '2023-01-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0002',
      hrid: 'EM0002',
      project: _getProjSelectItemDataById('XXP0002', projList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Emailed Cogent re New Drop',
      body: 'This is the second email body associated with E-LAN Migration',
      notes: 'This is the second email associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-02T18:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-02-02T18:15:00.000Z',
      modifiedAt: '2023-02-02T18:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0003',
      hrid: 'EM0003',
      location: _getLSelectItemDataById('XXL0001', locList),
      contact: _getCCTDataById('XXCCT0001', cctList),
      toAddresses: ['isaac@metropolisbank.com'],
      subject: 'Checked zoning issue',
      body: 'This is the first email body associated with this location',
      notes: 'This is the first email associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T19:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-01-02T19:15:00.000Z',
      modifiedAt: '2023-01-02T19:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0004',
      hrid: 'EM0004',
      location: _getLSelectItemDataById('XXL0001', locList),
      contact: _getCCTDataById('XXCCT0001', cctList),
      toAddresses: ['isaac@metropolisbank.com'],
      subject: 'Verified parking',
      body: 'This is the second email body associated with this location',
      notes: 'This is the second email associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T20:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-01-02T20:15:00.000Z',
      modifiedAt: '2023-01-02T20:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0005',
      hrid: 'EM0005',
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Verified SSN',
      body: 'This is the first email associated with Robert Downy, Jr.',
      notes: 'This is the first email associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-02T21:15:00.000Z',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-02-02T21:15:00.000Z',
      modifiedAt: '2023-01-02T21:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0006',
      hrid: 'EM0006',
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Got acnt for direct deposit',
      body: 'This is the second email associated with Robert Downy, Jr.',
      notes: 'This is the second email associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T22:15:00.000Z',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-01-02T22:15:00.000Z',
      modifiedAt: '2023-01-02T22:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0007',
      hrid: 'EM0007',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      contact: _getSCTDataById('XXSCT0002', sctList),
      toAddresses: ['james@cogent.com'],
      subject: 'Checked DIA availability',
      body: 'This is the first email body associated with Supplier Cogent',
      notes: 'This is the first email associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T23:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-01-02T23:15:00.000Z',
      modifiedAt: '2023-01-02T23:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0008',
      hrid: 'EM0008',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      contact: _getSCTDataById('XXSCT0002', sctList),
      toAddresses: ['james@cogent.com'],
      subject: 'Verified router model',
      body: 'This is the second email body associated with Supplier Cogent',
      notes: 'This is the second email associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-03T00:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-02-03T00:15:00.000Z',
      modifiedAt: '2023-02-28T00:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0009',
      hrid: 'EM0009',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      contact: _getSCTDataById('XXSCT0003', sctList),
      toAddresses: ['jeffrey@comcast.com'],
      subject: 'Checked ETA on router',
      body: 'This is the first email body associated with Supplier Comcast',
      notes: 'This is the first email associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-03T01:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-02-03T01:15:00.000Z',
      modifiedAt: '2023-02-28T01:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0010',
      hrid: 'EM0010',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      contact: _getSCTDataById('XXSCT0003', sctList),
      toAddresses: ['jeffrey@comcast.com'],
      subject: 'Got new billing contact',
      body: 'This is the second email body associated with Supplier Comcast',
      notes: 'This is the second email associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-03T02:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-02-03T02:15:00.000Z',
      modifiedAt: '2023-02-28T02:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0011',
      hrid: 'EM0011',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      contact: _getTCTDataById('XXTCT0002', tctList),
      toAddresses: ['babe@tbi.com'],
      subject: 'Verified resells Verizon',
      body: 'This is the first email body associated with Tsd TBI',
      notes: 'This is the first email associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-03T03:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-02-03T03:15:00.000Z',
      modifiedAt: '2023-02-28T03:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0012',
      hrid: 'EM0012',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      contact: _getTCTDataById('XXTCT0002', tctList),
      toAddresses: ['babe@tbi.com'],
      subject: 'Got new reps name',
      body: 'This is the second email body associated with Tsd TBI',
      notes: 'This is the second email associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-01-02T17:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-01-02T17:15:00.000Z',
      modifiedAt: '2023-01-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0013',
      hrid: 'EM0013',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Got cust liason name',
      body: 'This is the first email body associated with opportunity O00003',
      notes: 'This is the first email associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-02T17:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-02-02T17:15:00.000Z',
      modifiedAt: '2023-02-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0014',
      hrid: 'EM0014',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Checked DIA need',
      body: 'This is the second email body associated with opportunity O00003',
      notes: 'This is the second email associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-03-01T17:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-03-01T17:15:00.000Z',
      modifiedAt: '2023-03-01T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0015',
      hrid: 'EM0015',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Got location liason name',
      body: 'This is the first email body associated with opportunity ORD0003',
      notes: 'This is the first email associated with order ORD0003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-02-02T17:15:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-02-02T17:15:00.000Z',
      modifiedAt: '2023-02-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXEM0016',
      hrid: 'EM0016',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Memorialize Install plan',
      body: 'This is the second email body associated with opportunity ORD0003',
      notes: 'This is the second email associated with order ORD0003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-03-01T17:15:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getEmailStatusById(EmailStatusIds.CLOSED),
      createdAt: '2023-03-01T17:15:00.000Z',
      modifiedAt: '2023-03-01T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    // The one seed Cynque SENT (cynque-api-server #1770): a message, so its
    // envelope is fixed, it may only move to Needs Follow Up or Closed, and it
    // cannot be deleted. On the order the email e2e specs open.
    {
      id: 'XXEM0017',
      hrid: 'EM0017',
      // The helper is typed ISelectItem (id: string | number); the seeds above
      // carry that mismatch already, and this one need not add to it.
      order: _getMOSelectItemDataById('XXORD0001', moList) as IEntitySelectItem,
      fromAddress: 'sales@mail.mock.cynque.app',
      fromName: 'WX Technology Group',
      toAddresses: ['bilbo@bostoncoach.com'],
      subject: 'Install date confirmed',
      body: 'The install is confirmed for the 14th.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      sentAt: '2023-03-02T17:15:00.000Z',
      direction: 'outbound',
      deliveryState: 'delivered',
      messageId: '<XXEM0017@mail.mock.cynque.app>',
      attachments: [],
      owner: _getTMDataById('XXTM0001', tmList),
      status: getEmailStatusById(EmailStatusIds.SENT),
      createdAt: '2023-03-02T17:15:00.000Z',
      modifiedAt: '2023-03-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
  ]
}
